import React from "react";
import { CollisionPriority } from "@dnd-kit/abstract";
import { useDroppable } from "@dnd-kit/react";
import { Column, Row } from "@/shared/ui/Blocks";
import { SubtitleText } from "@/shared/ui/Typography";
import KanbanItem, { KanbanItemProps } from "./KanbanItem";
import { Card } from "@/shared/ui";
import { cn } from "@/shared/utils/shadcn";

export interface KanbanColumnProps extends React.HTMLProps<HTMLDivElement> {
  id: string;
  title?: string;
  subtitle?: string;
  items?: KanbanItemProps[];
  renderItem?: (item: KanbanItemProps) => React.ReactElement;
  disabled?: boolean;
  EmptyContentComponent?: React.ReactElement;
  HeaderRightComponent?: React.ReactElement; // Deprecated. Use HeaderComponent instead.
  HeaderComponent?: React.ReactElement;
  wrapperClassName?: string;
}

function KanbanColumn(props: KanbanColumnProps) {
  const {
    id,
    title,
    subtitle,
    HeaderRightComponent,
    HeaderComponent,
    EmptyContentComponent,
    items = [],
    renderItem,
    className,
    wrapperClassName,
  } = props;

  const { ref } = useDroppable({
    id,
    type: "column",
    accept: "item",
    collisionPriority: CollisionPriority.Low,
  });

  function ColumnHeader() {
    if (HeaderComponent) {
      return HeaderComponent;
    }

    return (
      <Card>
        <Row className="p-4 justify-between">
          <Column>
            <p>{title}</p>
            <SubtitleText>{subtitle}</SubtitleText>
          </Column>
          {HeaderRightComponent}
        </Row>
      </Card>
    );
  }

  return (
    <Column
      className={cn(
        "flex-1 min-w-[350px] min-h-[200px] max-h-[calc(100vh-200px)] max-w-[400px] overflow-y-hidden gap-4",
        wrapperClassName
      )}
    >
      <ColumnHeader />
      <div
        ref={ref}
        className={cn(
          "h-full p-2 bg-grey-100 rounded-md border border-solid overflow-y-auto border-grey-300",
          className
        )}
      >
        {items.map((item) => (
          <KanbanItem key={item.id} {...item}>
            {!!renderItem && renderItem(item)}
          </KanbanItem>
        ))}
        {items.length === 0 && EmptyContentComponent}
      </div>
    </Column>
  );
}

export default KanbanColumn;
